import { type VariantProps, cva } from 'class-variance-authority'

import * as React from 'react'

import { cn } from './utils'

/** Shopify badge: 20px tall, 6px radius, 12px medium label on a tinted fill. */
const badgeVariants = cva(
  'inline-flex h-5 min-w-5 items-center justify-center gap-1 whitespace-nowrap rounded-sm px-1.5 text-xs font-medium leading-none',
  {
    variants: {
      tone: {
        dark: 'bg-ink text-white',
        soft: 'bg-muted text-text-2',
        brand: 'bg-muted text-ink',
        success: 'bg-success-lighter text-success-dark',
        warning: 'bg-warning-lighter text-warning-dark',
        error: 'bg-error-lighter text-error-dark',
        info: 'bg-info-lighter text-info-dark',
        orange: 'bg-cta-lighter text-cta-dark',
        outline: 'border border-border text-ink',
      },
    },
    defaultVariants: {
      tone: 'soft',
    },
  }
)

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>

/** Legacy shadcn variants, mapped onto tones. */
const variantTone: Record<string, BadgeTone> = {
  default: 'brand',
  secondary: 'soft',
  destructive: 'error',
  outline: 'outline',
}

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  variant?: 'default' | 'secondary' | 'destructive' | 'outline'
}

function Badge({ className, tone, variant, ...props }: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ tone: tone ?? (variant ? variantTone[variant] : undefined) }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
